// POS-I227: the parts of a receipt that the Settings preview must show exactly as the real receipt
// prints them. ReceiptView and settings/ReceiptPreview both render these two components, so the
// preview cannot drift from the printed receipt.

import { RECEIPT_LAYOUT_DEFAULTS, type ReceiptLayout } from '@/types/pos';

export interface ReceiptLocation {
  name?: string | null;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
}

/** Logo, business name, tagline, seller NTN and the location's name, address and phone. */
export function ReceiptIdentity({
  logoUrl,
  name,
  tagline,
  ntn,
  location,
  logoPlaceholder,
  layout = RECEIPT_LAYOUT_DEFAULTS,
}: {
  logoUrl?: string | null;
  name?: string | null;
  tagline?: string | null;
  ntn?: string | null;
  location?: ReceiptLocation | null;
  /** Preview only: shows an empty logo box when no logo is set. */
  logoPlaceholder?: boolean;
  /** POS-I220: which parts print; default all. */
  layout?: ReceiptLayout;
}) {
  return (
    <>
      {layout.showLogo && (logoUrl ? (
        <img src={logoUrl} alt="Logo" className="h-12 mx-auto mb-2 object-contain" onError={(e) => (e.currentTarget.style.display = 'none')} />
      ) : (
        logoPlaceholder && <div aria-hidden className="mx-auto mb-2 h-12 w-24 rounded border border-dashed border-muted-foreground/40" />
      ))}
      <h2 className="text-lg font-bold break-words">{name}</h2>
      {layout.showTagline && tagline && <p className="text-xs text-muted-foreground">{tagline}</p>}
      {layout.headerNote && <p className="text-xs whitespace-pre-line break-words">{layout.headerNote}</p>}
      {layout.showNtn && ntn && <p className="text-xs text-muted-foreground">NTN: <bdi dir="ltr">{ntn}</bdi></p>}
      {location && (
        <>
          <p className="text-xs font-medium text-muted-foreground">{location.name}</p>
          {layout.showAddress && location.address && (
            <p className="text-xs text-muted-foreground">{location.address}{location.city ? `, ${location.city}` : ''}</p>
          )}
          {layout.showPhone && location.phone && (
            <p className="text-xs text-muted-foreground">Tel: {location.phone}</p>
          )}
        </>
      )}
    </>
  );
}

/** The thank-you / footer text; line breaks typed in Settings print as line breaks. */
export function ReceiptFooterText({ footer, fallback }: { footer?: string | null; fallback: string }) {
  return footer ? (
    <p className="mb-1 text-muted-foreground whitespace-pre-line">{footer}</p>
  ) : (
    <p>{fallback}</p>
  );
}

/** POS-I220: the owner's return policy, printed under the footer text. */
export function ReceiptReturnPolicy({ text }: { text?: string | null }) {
  return text ? <p className="mt-2 whitespace-pre-line break-words text-xs">{text}</p> : null;
}

/** POS-I220: the "Cashier: name" line under the invoice number and date. */
export function ReceiptCashierLine({ label, name }: { label: string; name?: string | null }) {
  return name ? <p className="text-xs text-muted-foreground">{label}: {name}</p> : null;
}
